import axios from 'axios';
import { createQuery } from 'react-query-kit';

import { restUrl } from '@/services/utils';
import { ItemsQuery } from '@/types/ItemsQuery';

import { FILTER_TYPE } from '../types';

export interface UserDefinedFilter {
    id?: string;
    name: string;
    label?: string;
    isPublic?: boolean;
    readOnly?: boolean;
    itemsQuery?: Partial<ItemsQuery>;
    type: typeof FILTER_TYPE.USER_DEFINED;
}

export interface DataModelDefinedFilter {
    name: string;
    label?: string;
    query: string;
    type: typeof FILTER_TYPE.DATA_MODEL_DEFINED;
}

export type SavedBrowseFilter = UserDefinedFilter | DataModelDefinedFilter;

function isUserDefinedFilter(filter: SavedBrowseFilter): filter is UserDefinedFilter {
    return filter.type === FILTER_TYPE.USER_DEFINED;
}

/**
 * User-saved filter profiles (`GET /filterProfiles`) plus data-model filters
 * (`GET /user/filters`). Mirrors
 * `items-search-query-controller.js#loadItemsSearchFilters`.
 */
export const useFiltersService = createQuery<SavedBrowseFilter[]>({
    queryKey: ['browse', 'filters'],
    fetcher: async () => {
        const [userProfiles, dataModelFilters] = await Promise.all([
            axios.get<UserDefinedFilter[]>(restUrl('filterProfiles')),
            axios.get<Array<{ name: string; label?: string; query: string }>>(
                restUrl('user/filters'),
            ),
        ]);

        const userDefined: UserDefinedFilter[] = (userProfiles.data ?? []).map((filter) => ({
            ...filter,
            label: filter.name,
            type: FILTER_TYPE.USER_DEFINED,
        }));

        const dataModelDefined: DataModelDefinedFilter[] = (dataModelFilters.data ?? []).map(
            (filter) => ({
                name: filter.name,
                label: filter.label || filter.name,
                query: filter.query,
                type: FILTER_TYPE.DATA_MODEL_DEFINED,
            }),
        );

        return [...userDefined, ...dataModelDefined];
    },
});

export { isUserDefinedFilter };
